<template>
	<template v-if="visible">
		<scroll-frame ref="agendaContainer">
			<template v-if="hasHead" #head><component :is="componentOfPart(ag.header)" :part="partRef(ag.header)" :data="data" :readonly="ro" :viewmode="vm"></component></template>
			<template #default>
				<table class="agenda" :class="clazz" :style="tableStyle" ref="itemsTable">
					<colgroup v-if="structure.colStyles"><col v-for="cs in structure.colStyles" :style="cs"></colgroup>
					<thead>
					<tr v-for="r in structure.header">
						<component v-for="c in r.cells" :is="c.component" v-bind="cellBind(c)"></component>
					</tr>
					</thead>
					<tbody ref="itemsBody" style="position:relative">
					<agenda-row v-for="(rm, idx) in array" :item-key="primaryKeyOf(rm)" :primary-key="primaryKeyOf(rm)" :object="rm" :index="idx">
						<tr v-for="r in structure['row']" :class="{ 'oddrow':(idx%2 === 1), 'evenrow':(idx%2 === 0) }" :data-idx="idx">
							<component v-for="c in r.cells" :is="c.component" :object="rm" v-bind="cellBind(c)"></component>
						</tr>
					</agenda-row>
					</tbody>
				</table>
			</template>
			<template #tail>
				<agenda-pager v-if="bottomPager" :span="span" :page="pageSize" :total="total" v-model.number="position"></agenda-pager>
			</template>
		</scroll-frame>
	</template>
</template>

<script setup lang="ts">
import useContainer, {type ContainerProps, containerProps} from "./Container";
import {connectEventBus, injectEnv} from "../Environment";
import {AGENDA_DIRTY, AgendaDefinition} from "@t/fd/layout/Agenda";
import {computed, CSSProperties, onMounted, onUnmounted, provide, ref} from "vue";
import {AGENDA_START_PADDING, useDataGrid} from "./agenda/AgendaUtils";
import AgendaPager from "./agenda/AgendaPager.vue";
import createSelected  from "./agenda/RowSelection";
import {getElementMeasure} from "@fd/helpers/DomUtils.ts";
import ScrollFrame from "@fd/containers/agenda/ScrollFrame.vue";
import AgendaRow from "@fd/containers/agenda/AgendaRow.vue";

const uprops = defineProps({...containerProps});
const props = uprops as ContainerProps;
const env = injectEnv();
const bus = connectEventBus();

const agendaContainer = ref(null);
const containerWidth = ref<number>(0);	// net width of the container
const ag = computed<AgendaDefinition>(() => props.part as AgendaDefinition);
const selected = createSelected();

const {visible, visibilityEval, clazz, style, componentOfPart, partRef, localData, ro, vm, prop, formData} = useContainer(props);

const hasHead = computed<boolean>(() => !!ag.value.header);

const paginated = computed<boolean>(() => true);
const bottomPager = computed<boolean>(() => paginated.value);

function action(name: string, key:number|string, event: MouseEvent): void {
	console.log("CELL ACTION: " + name + " shift=" + event.shiftKey);
}

const { structure, cellBind, array, total, position, pageSize, primaryKeyOf, dirty } = useDataGrid(ag, ro, localData, formData, action);

bus.on(AGENDA_DIRTY, (name:string) => {
	if (ag.value.name === name) dirty();
});

const span = computed<number>(() => structure.value.cols);

const selectable = computed<boolean>(() => {
    return !!ag.value.selectable && Array.isArray(ag.value.identity) && ag.value.identity.length > 0
});

const tableStyle = computed<CSSProperties>(() => {
	if (structure.value.tableStyle) {
		return { ...style.value, ...structure.value.tableStyle };
	}
	return style.value;
});


function measureContainer(container:HTMLDivElement) {
	if (container != null) {
		const m = getElementMeasure(container);
		containerWidth.value = Math.floor(m.width - (2 * AGENDA_START_PADDING));
	}
}

let resizeObserver = null;

function resizeCallback(entries:ResizeObserverEntry[]) {
	if (entries.length > 0) {
		measureContainer(entries[0].target as HTMLDivElement);
	}
}

function startObserving(el:HTMLDivElement) {
	if (resizeObserver == null) {
		resizeObserver = new ResizeObserver(resizeCallback);
		resizeObserver.observe(el);
		measureContainer(el);
	}
}

function stopObserving() {
	if (resizeObserver != null) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
}

onMounted(() => {
	startObserving(agendaContainer.value.frame);
});
onUnmounted(() => {
	stopObserving();
});
</script>

<style>
</style>
